import axiosInstance from 'utils/axiosUtil';
import { setChatbotState }        from '../../store/actions/appActions';
import {
  createChatBotMessage,
  createClientMessage
} from 'react-chatbot-kit-extension';
import routeUpdateState from './updateStateRouter';
import { CLAIMS_RANGE_MSG } from 'utils/claimsRange';
import { buildOpenReportContext } from './clientState';

/*
import CLAIMS_CFG from '../../../config/claims-date.js';   // adjust path if needed

const claimEnd = new Date(CLAIMS_CFG.latestClaimsDate ?? CLAIMS_CFG);
const claimStart   = new Date(claimEnd);
claimStart.setFullYear(claimEnd.getFullYear() - 1);

const CLAIMS_RANGE_MSG =
  'Claims data available from ' +
  `${claimStart.toISOString().slice(0,10)} through ` +
  `${claimEnd.toISOString().slice(0,10)}.`;

*/

export default class AudioConversation {

  constructor() {
    // this should be private only, use getInstance() to access
    // GA model - 'gpt-realtime' always points to latest, or pin to a snapshot:
    this.model = 'gpt-realtime-2.1';                    // was gpt-realtime-1.5; config now via session.update
    // temperature and speed removed - not supported in GA interface
    this.conversationSpecificPrompt = 'Speak with a warm, upbeat, and enthusiastic tone. Be energetic, positive, and sound like you genuinely enjoy helping. Never sound flat or monotone. Speak in English.';
    //this.conversationSpecificPrompt = 'You are a helpful, witty, and friendly AI.  
    // Your voice and personality should be warm and engaging, with a lively and playful tone. Speak quickly. Speak in English.';    

  // run-time fields
    this.peerConnection      = null;
    this.dataChannel         = null;
    this.localStream         = null;
    this.token               = null;
    this.sessionConfig       = null;   // full realtime config applied via session.update after connect
    this.endingConversation  = false;
    this.sessionId           = 0;
    this.initPromise         = null;
    this.pendingTool         = null;
    this._pendingTranscript  = null;   // text spoken in the current response, logged at response.done
    this._sessionInitialized = false;
    this._sessionReady       = false;
    this._lastLoggedResponseId = null; // dedup for the response.done usage log
    this._lastTranscriptId     = null; // dedup for the transcript UI append
    this.currentUrl          = typeof window !== 'undefined' ? window.location.pathname : '';

    this.autoShutdownMs = 90_000;    // 90 seconds
    this._shutdownTimer = null;

    this.state = { active: false, speaking: false, listening: false, thinking: false };

  }
  
  static getInstance() {
    if (!AudioConversation.instance) {
      AudioConversation.instance = new AudioConversation();
    }
    return AudioConversation.instance;
  }

  // helpers
  getState() {
    const s = this.state || { active: false, speaking: false, listening: false, thinking: false };
    return { ...s };
  }
  
  _clearShutdown() {
    if (this._shutdownTimer) {
      clearTimeout(this._shutdownTimer);
      this._shutdownTimer = null;
    }
  }

  // 90s of INACTIVITY: call this whenever there is activity to re-arm the timer
  _pokeShutdown() {
    // Don't keep arming if the session isn't up
    if (!this.peerConnection) return;
    this._clearShutdown();
    this._shutdownTimer = setTimeout(() => {
      if (!this.endingConversation) {
        console.log('[audio] auto-shutdown (90s inactivity)');
        this.endConversation();   // will say "Goodbye"
      }
    }, this.autoShutdownMs);
  }

  // Detect which tool (if any) was used in this turn
  _detectToolFromResponse(response) {
    const out = Array.isArray(response?.output) ? response.output : [];

    // 1) Function tools
    const fn = out.find((o) => o?.type === 'function_call');
    if (fn) {
     return { kind: 'function', name: fn.name || null, call_id: fn.call_id || null };
    }

    // 2) file_search (newer "tool_use" shape)
    const tu = out.find(
      (o) => o?.type === 'tool_use' && ((o.name || o.tool_name) === 'file_search')
    );
    if (tu) {
      return { kind: 'file_search', id: tu.id || null };
    }

    // 3) file_search (older shapes)
    const fs = out.find(
     (o) => typeof o?.type === 'string' && o.type.startsWith('file_search')
    );
    if (fs) {
      return { kind: 'file_search', id: fs.id || null };
    }

    // No tool used (pure text answer)
    return { kind: null };
  }

  async stopNow() {
    if (this._filterDialogHandler) { window.removeEventListener('ai-filter-dialog-state', this._filterDialogHandler); this._filterDialogHandler = null; }
    if (this._labelContextHandler) { window.removeEventListener('label-context-changed', this._labelContextHandler); this._labelContextHandler = null; }
    // cancel any shutdown/goodbye timers if you added them
    try { this._clearShutdown?.(); } catch {}
    try { this._clearGoodbyeTimers?.(); } catch {}

    // stop mic stream
    if (this.localStream) {
      try { this.localStream.getTracks().forEach((t) => t.stop()); } catch {}
      this.localStream = null;
    }

    // close RTC/data channel
    try { this.dataChannel?.close(); } catch {}
    this.dataChannel = null;

    try { this.peerConnection?.close(); } catch {}
    this.peerConnection = null;

    // reset state and notify UI
    this._setState?.({ active: false, speaking: false, listening: false, thinking: false });

    // clear flags
    this.endingConversation = false;
    this._sessionInitialized = false;
    this._sessionReady = false;
    this._lastLoggedResponseId = null;
    this._lastTranscriptId = null;
    this._pendingTranscript = null;
    this.pendingTool = null;
    this.sessionId++;
    AudioConversation.instance = null;
  }



  _emitState() {
    window.dispatchEvent(new CustomEvent('audio-state', { detail: this.getState() }));
  }
  
  _setState(patch) {
    this.state = { ...this.state, ...patch };
    this._emitState();
  }
  
  setCurrentUrl(url) {
    this.currentUrl = url || (typeof window !== 'undefined' ? window.location.pathname : '');
  }

  notifyFilterDialogState(open, labelName, namespaceName) {
    if (!this.isConversationStarted()) return;
    const text = open
      ? `[SYSTEM] The AI Provider Filter dialog is now OPEN for label "${labelName}" in group "${namespaceName}". The user has NOT navigated away.`
      : `[SYSTEM] The AI Provider Filter dialog has been CLOSED.`;
    this.sendMessage({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] } });
  }

  // Call this to update the model with the new URL during an active session
  async updateUrl(url) {
    this.setCurrentUrl(url);
    if (!this.isConversationStarted()) return;

    let contextText = `User changed path to: ${this.currentUrl}`;
    const labelMatch = this.currentUrl.match(/\/labels\/(\d+)/);
    if (labelMatch) {
      try {
        const tagId = parseInt(labelMatch[1], 10);
        const resp = await axiosInstance.post('/AI', { type: 'label-context-request', tag_id: tagId });
        if (resp.data?.tag_name) {
          contextText = `User navigated to label "${resp.data.tag_name}" in group "${resp.data.namespace_name}" (tag_id: ${tagId}) at path: ${this.currentUrl}`;
        }
      } catch (e) {
        console.warn('[updateUrl] label context lookup failed:', e.message);
      }
    }
    this.sendMessage({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: contextText }] },
    });
  }

  async init(id) {
    this.endingConversation = false;
    try {
      if (await this.requestMicrophonePermission()) {
        await this.getToken();
        await this.createConnection(id);
      } else {
        this._setState({ active: false, speaking: false, listening: false, error: 'microphone' });
        this._emitState();
      }
    } catch (err) {
      console.error('[AudioConversation] init error:', err);
      this._setState({ active: false, speaking: false, listening: false, error: err.message });
      this._emitState();
    }
  }

  async requestMicrophonePermission() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  }

  async getToken() {
    // Look up label context if on a labels page
    let labelContext = null;
    const labelMatch = this.currentUrl?.match(/\/labels\/(\d+)/);
    if (labelMatch) {
      try {
        const tagId = parseInt(labelMatch[1], 10);
        const r = await axiosInstance.post('/AI', { type: 'label-context-request', tag_id: tagId });
        if (r.data?.tag_name) labelContext = r.data;
      } catch (e) {
        console.warn('[getToken] label context lookup failed:', e.message);
      }
    }
    const resp = await axiosInstance.post('/AI', {
      type         : 'real-time-token-request',
      model        : this.model,
      current_url  : this.currentUrl,
      label_context: labelContext,
      user         : this.user,
      prompt       : await this.getPrompt(),
      tools        : await this.getTools(),
    });
    this.token = resp.data.token;
    // gpt-realtime-2.1: client secret carries only model+voice; the rest is applied
    // via session.update once the data channel opens (see onSessionCreated).
    this.sessionConfig = resp.data.sessionConfig || null;
  }

  async getPrompt() {
    return (await axiosInstance.post('/AI', {
      type: 'get-prompt',
    })).data.prompt;
  }

  async getTools() {
    return (await axiosInstance.post('/AI', {
      type: 'get-tools',
    })).data.tools;
  }


  async createConnection(id) {
    const aborted = () => id !== this.sessionId;
    try {
      this.peerConnection = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      if (aborted()) return;

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.playsInline = true;
      this.peerConnection.ontrack = (e) => { audioEl.srcObject = e.streams[0]; };

      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (aborted()) { this.localStream.getTracks().forEach(t => t.stop()); return; }
      this.localStream.getAudioTracks().forEach((track) => {
        this.peerConnection.addTrack(track, this.localStream);
        track.addEventListener('ended', () => {
          this._setState({ active: false, speaking: false, listening: false });
          this.checkEndConversation();
        });
      });

      this._setState({ active: true, speaking: false, listening: true });

      this.peerConnection.onconnectionstatechange = () => {
        const s = this.peerConnection.connectionState;
        if (s === 'connected') this._setState({ active: true, listening: true, speaking: false });
        else if (['disconnected', 'failed', 'closed'].includes(s)) { this._setState({ active: false, speaking: false, listening: false }); this._sessionInitialized = false; }
      };

      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.dataChannel.addEventListener('open', () => this.onDataChannelOpen());
      this.dataChannel.addEventListener('message', (e) => this.onDataChannelMessage(e));

      const offer = await this.peerConnection.createOffer({ offerToReceiveAudio: true, voiceActivityDetection: true });
      await this.peerConnection.setLocalDescription(offer);
      if (aborted()) return;

      // Call OpenAI directly using the ephemeral token (full session config already in token)
      const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
        method : 'POST',
        headers: {
          Authorization  : `Bearer ${this.token}`,
          'Content-Type' : 'application/sdp',
        },
        body: offer.sdp,
      });

      if (aborted()) return;
      if (!sdpResponse.ok) {
        const errText = await sdpResponse.text();
        throw new Error(`SDP exchange failed: ${sdpResponse.status} ${errText}`);
      }

      const answerSdp = await sdpResponse.text();
      await this.peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    } catch (err) {
      console.error('createConnection error:', err);
      try { this.dataChannel?.close(); } catch {} this.dataChannel = null;
      try { this.peerConnection?.close(); } catch {} this.peerConnection = null;
      if (this.localStream) { this.localStream.getTracks().forEach((t) => t.stop()); this.localStream = null; }
      this._setState({ active: false, speaking: false, listening: false });
      throw err;
    }
  }

    async OLDcreateConnection(id) {
     const aborted = () => id !== this.sessionId;

    // Create a peer connection
    this.peerConnection = new RTCPeerConnection();
    if (aborted()) return;

    // Set up to play remote audio from the model
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    this.peerConnection.ontrack = (e) => audioEl.srcObject = e.streams[0];

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    if (aborted()) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
      return;
    }

    this.localStream = ms;
    const track = ms.getTracks()[0];
    this.peerConnection.addTrack(track);

    // mic unplugged / permission revoked, etc.
    track.addEventListener('ended', () => {
      this._setState({ active: false, speaking: false, listening: false });
      this.checkEndConversation();
    });

    // connection state (network issues, ICE failure, etc.)
    this.peerConnection.onconnectionstatechange = () => {
      const s = this.peerConnection.connectionState;
      if (s === 'connected') {
        this._setState({ active: true, listening: true, speaking: false });
      } else if (['disconnected', 'failed', 'closed'].includes(s)) {
        this._setState({ active: false, speaking: false, listening: false });
      }
    };

    // Set up data channel for sending and receiving events
    this.dataChannel = this.peerConnection.createDataChannel('oai-events');
    this.dataChannel.addEventListener('open', () => { this.onDataChannelOpen(); });
    this.dataChannel.addEventListener('message', (e) => { this.onDataChannelMessage(e); });

    // Start the session using the Session Description Protocol (SDP)
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    
    if (aborted()) return;
    const baseUrl = 'https://api.openai.com/v1/realtime';
    const sdpResponse = await fetch(`${baseUrl}?model=${this.model}`, {
      method: 'POST',
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/sdp',
      },
    });
    if (aborted()) return;
    
    const answer = {
      type: 'answer',
      sdp: await sdpResponse.text(),
    };
    await this.peerConnection.setRemoteDescription(answer);
  }

  isConversationStarted() {
    return !!this.peerConnection;
  }

  startConversation(router, user, clientId, dispatchFn, initialState, getStateFn) {
    this.endingConversation = false;
    this.router = router;
    this.user = user;
    this.clientId = clientId;
    this.dispatch = dispatchFn;
    this._reduxGetState = getStateFn || null;
    this._filterDialogHandler = (e) => { this.notifyFilterDialogState(e.detail.open, e.detail.tagName, e.detail.namespaceName); };
    window.addEventListener('ai-filter-dialog-state', this._filterDialogHandler);
    this._labelContextHandler = (e) => {
      if (!this.isConversationStarted()) return;
      const { tagId, tagName, namespaceName } = e.detail;
      this.sendMessage({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `[SYSTEM] User is viewing label "${tagName}" in group "${namespaceName}" (tag_id: ${tagId}).` }] } });
    };
    window.addEventListener('label-context-changed', this._labelContextHandler);
    this.history = (initialState.messages || []).slice();
    this.setCurrentUrl(typeof window !== 'undefined' ? window.location.pathname : '');
    const id = ++this.sessionId;
    if (!this.peerConnection) {
      this.init(id);
    }
  }
    

  async endConversation() {
    // 0. prevent double-calls
    if (this.endingConversation) return;

    this.endingConversation = true;

    // Stop any further auto-shutdown triggers
    this._clearShutdown();

    // GA: modalities removed from response.create - not supported
    await this.sendMessage({
      type: 'response.create',
      response: {
        instructions: 'Say in English: Goodbye.',
      },
    });
  }  
 
 
  async checkEndConversation() {
    if (!this.endingConversation) return;
    if (this.endingConversation) {
      
      console.log('Ending session...');

      /* stop mic stream */
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
        this.localStream = null;
      }

    if (this.dataChannel)     { try { this.dataChannel.close(); }     catch {} this.dataChannel = null; }
    if (this.peerConnection)  { try { this.peerConnection.close(); }  catch {} this.peerConnection = null; }

    this._setState({ active: false, speaking: false, listening: false });

      this._clearShutdown();
      this.endingConversation = false;
      this._sessionInitialized = false;
      this._sessionReady = false;
      this._lastLoggedResponseId = null;
      this._lastTranscriptId = null;
      this._pendingTranscript = null;
      this.pendingTool = null;
      this.sessionId++;
      AudioConversation.instance = null;
    }
  }

  async onDataChannelOpen() {
    if (this._sessionInitialized) return;
    this._sessionInitialized = true;
    console.log('[audio] Data channel opened - sending greeting');
    await this.onSessionCreated();
  }
  
  async onDataChannelMessage(event) {
    try {
      const data = JSON.parse(event.data);
      if (!data.timestamp) {
        data.timestamp = new Date().toLocaleTimeString();
      }

      console.log('[DC EVENT]', data.type);

      // --- 1. good-bye detector --------------------------------------
      // GA: response.audio_transcript.done → response.output_audio_transcript.done
      if (
        data.type === 'response.output_audio_transcript.done' ||
        data.type === 'conversation.item.input_audio_transcription.completed'
      ) {
        const spoken =
          (data.transcript ||
          data.response?.output?.find((o) => o.type === 'text')?.text ||
          ''
          ).trim().toLowerCase();

        if (['goodbye', 'bye', 'good-bye', 'good bye'].includes(spoken)) {
          await this.endConversation();   // graceful hang-up
          return;                         // <- stop further processing
        }
        // FALL THROUGH so the normal handlers can still run
      }
    
      if (data.type === 'assistant.tool_call_created'
          && data.tool_call.name === 'search_private_docs') {

        this.pendingTool = 'function:search_private_docs';

        // ➊ read the arguments the model sent
        const { query, top_k = 3 } =
              JSON.parse(data.tool_call.arguments);

        // ➋ run your own vector search
        const passages = await axiosInstance.post(
          '/AI',
          { type:'execute-function',
            function_name:'search_private_docs',
            args:{ query, top_k },
            audio_mode:true }
        ).then((r) => r.data.passages ?? [])
         .catch(() => []);

        // ➌ return the result to the model
        await this.sendMessage({
          type: 'assistant.tool_result',
          tool_call_id: data.tool_call.id,
          content: JSON.stringify({ passages })
        });

      /* ---------------------------------------------------------*
      * 1.  assistant finished the turn  →  response.done
      * ---------------------------------------------------------*/      
      
      } else if (data.type === 'response.done') {
        // response.done is the LAST event of a response (it fires after the transcript event)
        // and carries the authoritative usage, model, and full output (tools + spoken text).
        // Do ALL usage logging HERE so every response is attributed correctly — a tool-using
        // voice turn is actually several responses (preamble → function calls → synthesis),
        // and logging off the earlier transcript event would misattribute or drop the tools.
        const usage     = data.response.usage || {};
        const turnModel = data.response.model || this.model;

        /* ∙∙∙ Did the model call one or more tools this response? */
        const fnCalls = (data.response.output || []).filter((o) => o.type === 'function_call');
        const toolLabel = fnCalls.length
          ? fnCalls.map((f) => `function:${f.name}`).join(',')
          : (this._detectToolFromResponse(data.response).kind || 'none');
        console.log(`[audio turn] tools=${toolLabel} calls=${fnCalls.length} input_toks=${usage.input_tokens ?? 0} output_toks=${usage.output_tokens ?? 0}`);
        this.pendingTool = toolLabel;

        // Log this response's usage. answer = text spoken in THIS response (empty for a
        // function-call-only response). Skip the 0-token placeholder greeting, and dedup
        // on the response id so a re-delivered event can't double-count.
        const answer = this._pendingTranscript || '';
        this._pendingTranscript = null;
        const isStrayGreeting = !this._sessionReady && (usage.input_tokens ?? 0) === 0;
        if (!isStrayGreeting && this._lastLoggedResponseId !== data.response.id) {
          this._lastLoggedResponseId = data.response.id;
          await axiosInstance.post('/AI', {
            type       : 'log-usage',
            user_id    : this.user.id,
            client_id  : this.clientId,
            current_url: this.currentUrl,
            context    : {
              mode       : 'Audio',
              model      : turnModel,
              tool       : toolLabel,
              input_toks : usage.input_tokens  ?? 0,
              output_toks: usage.output_tokens ?? 0,
              total_toks : usage.total_tokens  ?? 0,
              cached_toks: usage.input_tokens_details?.cached_tokens ?? 0,
              answer     : answer,
            },
          });
        }

        // Resolve tool calls AFTER logging so the usage record isn't held up by query latency.
        if (fnCalls.length > 0) {
          await this.handleFunctionsMessage(data);
        }

        this._pokeShutdown();           // assistant finished a turn

      // GA: response.audio_transcript.done → response.output_audio_transcript.done
      } else if (data.type === 'response.output_audio_transcript.done') {
        const transcript = data.transcript;

        // Dedup guard (some sessions redeliver the transcript event)
        const transcriptId = data.response_id || data.item_id || transcript;
        if (this._lastTranscriptId === transcriptId) {
          console.warn('[audio] duplicate transcript event, skipping');
          return;
        }
        this._lastTranscriptId = transcriptId;

        this._appendTranscript('assistant', transcript);
        // Stash the spoken text; the usage log is emitted at response.done (fires right
        // after this event) where usage/model/tool are all authoritative for this response.
        this._pendingTranscript = transcript;
        this._pokeShutdown();           // assistant transcript is ready

      } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
        // user finished talking; still active, now waiting for assistant (Key is "thinking")
        this._setState({ listening: false, thinking: true });
        console.log('user transcript done:', data.transcript);
        this._appendTranscript('user', data.transcript);
        const spoken = (data.transcript || '').trim().toLowerCase();
        if (spoken === 'close chat') { await this.endConversation(); return; }
        this._pokeShutdown();           // user finished talking

      } else if (data.type === 'session.created') {
        // GA: session setup now triggered from onDataChannelOpen instead
        console.log('[audio] session.created received (setup already triggered from channel open)');

      } else if (data.type === 'output_audio_buffer.stopped') {
        this._setState({ speaking: false, listening: this.state.active, thinking: false });
        window.dispatchEvent(new Event('assistant-silent'));
        this.checkEndConversation();

        this._pokeShutdown();           // assistant done speaking

      } else if (data.type === 'output_audio_buffer.started') {
        // assistant has started speaking — thinking window is over
        this._setState({ speaking: true, listening: false, thinking: false });
        window.dispatchEvent(new Event('assistant-speaking'));
        this._pokeShutdown();

      // GA: response.audio_transcript.delta → response.output_audio_transcript.delta
      } else if (data.type === 'response.output_audio_transcript.delta') {
        // ignore these

      } else if (data.type === 'error') {
        console.log('ERROR:', data);
        this._setState({ thinking: false });   // don't leave the thinking indicator stuck on

      } else {
        // console.log('unhandled:', data.type);
      }

    } catch (error) {
      console.error('Error processing data channel message:', error);
    }
  }

  async handleFunctionsMessage(data) {
    // gpt-realtime-2.1 can emit MULTIPLE function_calls in a single turn (parallel tool
    // calling). Resolve every one: each call_id needs its own function_call_output, then a
    // SINGLE response.create at the end so the model synthesizes all results into one reply.
    const fnCalls = (data.response.output || []).filter((o) => o.type === 'function_call');
    if (fnCalls.length === 0) return;

    // Sequential (not Promise.all) on purpose: the label-filter tools share a server-side
    // query cache keyed by user+client (global['labelFilter_*']) and mutate Redux filter
    // state, so concurrent execution would race. In-order keeps side effects deterministic.
    for (const fnCall of fnCalls) {
      let resultForAI;
      try {
        const args = typeof fnCall.arguments === 'string'
                     ? JSON.parse(fnCall.arguments)
                     : fnCall.arguments;
        const result = await this.onFunctionCall(fnCall.name, args);
        // Strip data payloads before sending the tool result to the realtime model. It must NOT
        // receive Doctivity data (provider/org names, PHI) — only success/message + name-free
        // aggregates. Rows ride in two vectors: top-level queryRows/columns/criteria AND the
        // update_state string (its launch_*/refine_* payload embeds rows WITH names). update_state
        // is frontend-only routing and onFunctionCall already applied it to Redux via
        // routeUpdateState before returning, so dropping it here doesn't affect the UI.
        const { queryRows, columns, criteria, update_state: _updateStateFrontendOnly, ...rest } = result || {};
        resultForAI = rest;
      } catch (err) {
        console.error(`[audio] function ${fnCall.name} failed:`, err);
        resultForAI = { error: err.message || 'function failed' };
      }

      // EVERY call_id must get an output — a missing one leaves the model waiting forever.
      await this.sendMessage({
        type: 'conversation.item.create',
        item: {
          type   : 'function_call_output',
          call_id: fnCall.call_id,
          output : JSON.stringify(resultForAI),
        },
      });
    }

    // One response.create after ALL outputs are in → a single synthesized spoken answer.
    await this.sendMessage({ type: 'response.create' });
  }

  async onFunctionCall(name, args) {
    /* take_me_there is handled locally */
    if (name === 'take_me_there') {
      if (args?.path) {
        this.router?.navigate(args.path);
        // Update session URL context after navigation
        const abs = typeof window !== 'undefined'
          ? window.location.origin + args.path
          : args.path;
        this.updateUrl(abs);
        
        /* tell the server so it can log the action */
        axiosInstance.post('/AI', {
          type          : 'execute-function',
          function_name : 'take_me_there',
          args          : { path: args.path, audio_mode:true },
          user_id       : this.user.id,
          client_id     : this.clientId,
          current_url   : this.currentUrl
        }).catch((e) => console.warn('[audio] take_me_there log failed:', e));

        return { success: true, message: `Navigated to ${args.path}` };
      }
      return { error: 'Path was not provided.' };
    }

    /* For refine_label_filter, override current_criteria with live Redux state
       so manual chip removals are reflected even though AI doesn't know about them */
    let finalArgs = args;
    if (name === 'refine_label_filter' || name === 'refine_org_filter') {
      const liveState = this._reduxGetState?.();
      const liveFilter = liveState?.app?.aiLabelFilter;
      const liveCurrentQuery = liveFilter?.currentQuery;
      const liveCriteria = liveFilter?.criteria;
      console.log('[audio] refine injection: hasGetState=', !!this._reduxGetState, 'currentQuery=', !!liveCurrentQuery, 'criteria=', !!liveCriteria);
      if (liveCurrentQuery && typeof liveCurrentQuery === 'object') {
        finalArgs = { ...args, current_criteria: JSON.stringify(liveCurrentQuery) };
      } else if (liveCriteria && typeof liveCriteria === 'object') {
        finalArgs = { ...args, current_criteria: JSON.stringify(liveCriteria) };
      } else {
        finalArgs = { ...args, current_criteria: null };
      }
    }

    /* all other functions -> back-end */
    const isFilterFn = ['launch_label_filter','launch_org_filter','refine_label_filter','refine_org_filter'].includes(name);
    if (isFilterFn) window.dispatchEvent(new CustomEvent('ai-filter-fetching', { detail: { fetching: true } }));

    const resp = await axiosInstance.post('/AI', {
      type          : 'execute-function',
      function_name : name,
      args          : finalArgs,
      user_id       : this.user.id,
      client_id     : this.clientId,
      audio_mode    : true,
      current_url   : this.currentUrl,
      // The market the user is currently viewing (state.app.market) — lets market-scoped recipes
      // default to it when the user says "my market" without naming one.
      current_market: this._reduxGetState?.()?.app?.market ?? null,
      // Open report's spec (no rows) — lets the backend rebuild its per-worker report cache.
      open_report   : buildOpenReportContext(this._reduxGetState?.()?.app?.aiReport),
    });

    if (isFilterFn) window.dispatchEvent(new CustomEvent('ai-filter-fetching', { detail: { fetching: false } }));

    if (resp.status !== 200 || !resp.data)
      return { error: `Failed to execute: ${name}` };

    routeUpdateState(
      resp.data.update_state, this.dispatch, this.clientId, this.router,
      () => this._reduxGetState?.()?.app,
    );
    return resp.data;
  }
 
  _appendTranscript(role, text) {
    if (!text || !text.trim() || !this.dispatch) return;

    const nextMessage =
      role === 'user'
        ? createClientMessage(text)
        : createChatBotMessage(text, { loading:false });

    this.history = [...this.history, nextMessage];

    /* 1. write the growing array to Redux so the panel that is
          currently open sees the new line immediately            */
    this.dispatch(setChatbotState({ messages: this.history}));

    /* 2. also broadcast how many messages are now stored so the
          next ActionProvider can fetch them when it mounts       */
    window.sessionStorage.setItem(
      'audio-history-length',
      String(this.history.length),
    );
  }

  async onSessionCreated() {
    try {
      console.log('[audio] onSessionCreated START');
      // gpt-realtime-2.1: the client secret only pinned model+voice, so apply the full
      // session config (instructions, tools, turn detection, streaming transcription) now,
      // before the greeting. session.update may set any field except model/voice.
      if (this.sessionConfig) {
        await this.sendMessage({ type: 'session.update', session: this.sessionConfig });
        console.log('[audio] session.update sent, instructions length:', this.sessionConfig.instructions?.length);
      }

      let today = new Date();
      let curHr = today.getHours();
      let time = curHr < 12 ? 'morning' : curHr < 18 ? 'afternoon' : 'evening';

      const firstQuestion =
        `Say: 'Good ${time} ` +
        `${this.user.first_name_pronunciation?.trim() || this.user.first_name}. ` +
        "My name is Key, Doctivity's AI assistant. How may I help you?'";

      // GA: modalities removed from response.create - not supported
      await this.sendMessage({
        type: 'response.create',
        response: {
          instructions: firstQuestion,
        },
      });
      console.log('[audio] response.create SENT (greeting)');
      this._sessionReady = true;

      // Arm the inactivity timer once the session is ready
      this._pokeShutdown();

    } catch (error) {
      console.error('Error during session creation:', error);
    }
  }

  async sendMessage(message) {
    message.event_id = message.event_id || crypto.randomUUID();
    console.log('sending message:', message);
    this.dataChannel?.send(JSON.stringify(message));
  }  

}